import { useQueryClient } from "@tanstack/react-query";
import { Spin } from "antd";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { IHospitalConfiguration, IUserDetails } from "../../../@types/Login";
import { IPortalPreferences } from "../../../@types/Preferences";
import { IRoutePattern } from "../../../@types/Route";
import { ArrowDown } from "../../../assets/icons/ArrowDown";
import { Checkbox } from "../../../components/Checkbox";
import { ErrorBoundary } from "../../../components/ErrorBoundary";
import { SearchBar } from "../../../components/SearchBar";
import { IOption } from "../../../components/SelectDropdown";
import { SupportButton } from "../../../components/SupportButton";
import {
  TopPanelMenuComponent,
  TopPanelMenuMobileButtonComponent,
} from "../../../components/TopPanelMenu";
import { UserSettingsDesktop } from "../../../components/UserSettings/UserSettings";
import {
  AUTH_HOSPITAL_CONFIGURATION,
  AUTH_PORTAL_PREFERENCES,
  AUTH_STORED_ROUTE,
  AUTH_USER_DETAILS,
  IS_MOBILE_APP,
  PATIENT_LIST_VARIANT,
} from "../../../constants/localStorageKeys";
import {
  AEROGEN_EMAIL_DOMAIN,
  PMP_EMAIL_DOMAIN,
} from "../../../constants/pMpConstants";
import { PATIENT_SEARCH_LIST_QUERY_KEY } from "../../../hooks/queries/patients";
import { useAuth } from "../../../hooks/useAuth";
import { useChildRoutes } from "../../../hooks/useChildRoutes";
import { useCurrentUserRole } from "../../../hooks/useCurrentUserRole";
import useIsMobile from "../../../hooks/useIsMobile";
import { useLeftPanel } from "../../../hooks/useLeftPanel";
import { useUserPreferences } from "../../../hooks/useUserPreferences";
import { useWardLabel } from "../../../hooks/useWardLabel";
import { NotFoundContent } from "../../../pages/NotFound";
import { spinCss } from "../../../pages/Surveys/Answer Survey/Components/Survey/AnswerSurvey.styles";
import { mainRoutes } from "../../../routes/main.routes";
import { userService } from "../../../services/userService";
import { wardsService } from "../../../services/wardsService";
import { isSystemControllerUser } from "../../../utils/isSystemControllerUser";
import { mixpanelActions } from "../../../utils/mixpanel";
import { safeStorage, safeJsonParse } from "../../../utils/storage";
import { LeftPanelMenu } from "../LeftPanelMenu";
import { LeftPanelUserSettings } from "../LeftPanelUserSettings";
import { Link } from "../Link";
import { ViewTypeSelector } from "../ViewTypeSelector/ViewTypeSelector";
import {
  ContentContainer,
  DisplayName,
  NavContentContainer,
  OptionsContainer,
  TopPanelItemsContainerDesktop,
  WardOption,
  WardSelector,
} from "./DashboardLayout.styles";

type DashboardProps = {
  leftPanelHidden?: boolean;
  // Rendered in place of the routed outlet. Lets a page that isn't a child
  // route reuse the real portal chrome — the 404 page (EN-2750) renders itself
  // inside this layout rather than duplicating the top panel.
  children?: ReactNode;
};

type SelectedOption = {
  id: string;
  value: boolean;
};

export function DashboardLayout({
  leftPanelHidden = false,
  children,
}: DashboardProps) {
  const { t } = useTranslation();
  const wardLabel = useWardLabel();
  const [wardData] = useState<IOption[]>([]);

  const [selectedOptions, setSelectedOptions] = useState<SelectedOption[]>([]);
  const [showOptions, setShowOptions] = useState(false);
  const [options, setOptions] = useState<any[]>([]);

  const [showMulitpleWards, setShowMulitpleWards] = useState(false);
  const [update, setUpdate] = useState(true);

  const optionRef = useRef<HTMLDivElement>(null);

  const { childRoutes } = useChildRoutes();

  const { isAuthenticated } = useAuth();
  const { changePortalPreferences } = useUserPreferences();
  const {
    isMobileLeftPanelMenuOpen,
    setIsMobileLeftPanelMenuOpen,
    isMobileUserSettingsMenuOpen,
    setIsMobileUserSettingsMenuOpen,
  } = useLeftPanel();
  const { isMobile, isSmallScreen } = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { canViewRagTab } = useCurrentUserRole();

  if (!isAuthenticated) {
    safeStorage.setItem(
      AUTH_STORED_ROUTE,
      `${location.pathname}${location.search}`
    );
    return <Navigate to="/login" />;
  }

  // EN-4961: SystemController users only have access to the reports
  // dashboard. If they land on a regular-portal route (e.g. by bookmark or
  // direct URL), bounce them to /reports instead of letting the page render
  // with no permitted data. Role identity rather than the
  // admin-reports:read permission so future roles that also receive the
  // permission (e.g. HealthcareAdmin in Phase 2) keep regular-portal access.
  if (isSystemControllerUser()) {
    return <Navigate to="/reports" replace />;
  }

  const queryClient = useQueryClient();

  useEffect(() => {
    const url = window.location.href.split("/");

    if (url[3] === "patients") {
      if (
        url[4] === "archived" ||
        url[4] === "active" ||
        url[4] === "flagged" ||
        url[4] === "alerts"
      ) {
        queryClient.refetchQueries({
          queryKey: [`patient-list-${url[4]}`],
        });
      } else if (url[4] === "billing") {
        queryClient.refetchQueries({ queryKey: ["patient-list-billing"] });
      }
    }
  }, [window.location.href]);

  const handleOnChangeMobileLeftPanelVisibility = () => {
    if (isMobileUserSettingsMenuOpen) {
      setIsMobileUserSettingsMenuOpen(false);
    } else {
      setIsMobileLeftPanelMenuOpen(
        (prevMobileLeftPanelMenuState) => !prevMobileLeftPanelMenuState
      );
    }
  };

  const handlePatientListNavigation = () => {
    const localStorageVariant = safeStorage.getItem(PATIENT_LIST_VARIANT);

    navigate(`/patients/${localStorageVariant}`);
  };

  const renderTopBarMenuRoutes = () => {
    const handlePathNavigation = (path: string) => {
      if (path.includes("patients")) {
        const localStorageVariant = safeStorage.getItem(PATIENT_LIST_VARIANT);

        return `/patients/${localStorageVariant}`;
      }

      return path;
    };

    const tempRoutes = mainRoutes;

    const parsedUserDetails = safeJsonParse<IUserDetails>(
      safeStorage.getItem(AUTH_USER_DETAILS),
      {} as IUserDetails
    );

    if (
      parsedUserDetails.userEmail &&
      parsedUserDetails.userEmail.includes(PMP_EMAIL_DOMAIN)
    ) {
      const surveysIndex = tempRoutes.findIndex((x) => x.name === "Surveys");
      if (surveysIndex !== -1) {
        tempRoutes[surveysIndex].disabled = false;
      }

      const protocolsIndex = tempRoutes.findIndex((x) => x.name === "Protocol");
      if (protocolsIndex !== -1) {
        tempRoutes[protocolsIndex].disabled = false;
      }

      const careInfoIndex = tempRoutes.findIndex((x) => x.name === "Care Info");
      if (careInfoIndex !== -1) {
        tempRoutes[careInfoIndex].disabled = false;
      }

      const workflowsIndex = tempRoutes.findIndex(
        (x) => x.name === "Workflows"
      );
      if (careInfoIndex !== -1) {
        tempRoutes[workflowsIndex].disabled = false;
      }
    }

    // Enable Protocol and Care Info tabs for Aerogen users
    if (
      parsedUserDetails.userEmail &&
      parsedUserDetails.userEmail.includes(AEROGEN_EMAIL_DOMAIN)
    ) {
      const protocolsIndex = tempRoutes.findIndex((x) => x.name === "Protocol");
      if (protocolsIndex !== -1) {
        tempRoutes[protocolsIndex].disabled = false;
      }

      const careInfoIndex = tempRoutes.findIndex((x) => x.name === "Care Info");
      if (careInfoIndex !== -1) {
        tempRoutes[careInfoIndex].disabled = false;
      }
    }

    const parsedHospitalConfiguration = safeJsonParse<IHospitalConfiguration>(
      safeStorage.getItem(AUTH_HOSPITAL_CONFIGURATION),
      {} as IHospitalConfiguration
    );

    if (parsedHospitalConfiguration.enableAppointments) {
      const appointmentsIndex = tempRoutes.findIndex(
        (x) => x.name === "Appointments"
      );

      if (appointmentsIndex !== -1) {
        tempRoutes[appointmentsIndex].disabled = false;
      }
    }

    if (parsedHospitalConfiguration.enableNebulizerAdherence) {
      const dashboardIndex = tempRoutes.findIndex(
        (x) => x.name === "Dashboard"
      );

      if (dashboardIndex !== -1) {
        tempRoutes[dashboardIndex].disabled = false;
      }
    }

    if (parsedHospitalConfiguration.enableCareInfo) {
      const careInfoIndex = tempRoutes.findIndex((x) => x.name === "Care Info");
      if (careInfoIndex !== -1) {
        tempRoutes[careInfoIndex].disabled = false;
      }
    }

    if (canViewRagTab) {
      const ragIndex = tempRoutes.findIndex((x) => x.name === "RAG");
      if (ragIndex !== -1) {
        tempRoutes[ragIndex].disabled = false;
      }
    }

    return tempRoutes
      .filter(({ disabled }) => !disabled)
      .map(({ path, name, labelKey, newTab }) => (
        <Link
          key={`${Math.random()}-${path}`}
          to={handlePathNavigation(path)}
          newTab={newTab}
        >
          {labelKey ? t(labelKey) : name}
        </Link>
      ));
  };

  const renderPatientListChildroutes = () => {
    const parsedHospitalConfig = safeJsonParse<
      IHospitalConfiguration | undefined
    >(safeStorage.getItem(AUTH_HOSPITAL_CONFIGURATION), undefined);

    if (!parsedHospitalConfig?.enableBilling) {
      return childRoutes;
    }

    // EN-5199: return a copy instead of mutating the context's shared route
    // objects during render.
    return childRoutes.map((route) =>
      route.name === "Billing" ? { ...route, hidden: false } : route
    );
  };

  const renderLeftPanelMenuByDevice = (): IRoutePattern[] => {
    return isMobile || isSmallScreen
      ? mainRoutes
      : renderPatientListChildroutes();
  };

  const handleUserTracking = () => {
    const selectedIds: string[] = [];

    selectedOptions.forEach((option) => {
      if (option.id === "all" && option.value) {
        wardData.forEach((ward) => {
          selectedIds.push(ward.value);
        });
      }

      if (option.id !== "all" && option.value) {
        selectedIds.push(option.id);
      }
    });

    const selectedHospitalsNames = wardData
      .filter((x) => selectedIds.includes(x.value))
      .map((x) => x.label)
      .join(", ");

    mixpanelActions.track(
      `User Action: ChangeWard (${selectedHospitalsNames})`
    );
  };

  const handleWardOptions = (): void => {
    const optionsToSet: any[] = [];

    if (!selectedOptions.length) {
      return;
    }

    optionsToSet.push(
      <WardOption
        selected={selectedOptions.find((x) => x.id === "all")?.value}
        key="all-wards-key"
      >
        <Checkbox
          checked={selectedOptions.find((x) => x.id === "all")?.value ?? false}
          label=""
          onChange={(isChecked) => {
            const tempOptions = [...selectedOptions];

            tempOptions[0].value = isChecked;

            setSelectedOptions(tempOptions);

            handleUserTracking();
          }}
          disabled={selectedOptions[0].value}
        />
        <p>{t("nav_allWards", { wards: wardLabel.plural })}</p>
      </WardOption>
    );

    wardData.forEach((option) => {
      optionsToSet.push(
        <WardOption
          selected={selectedOptions.find((x) => x.id === option.value)?.value}
          key={option.key}
        >
          <Checkbox
            checked={
              selectedOptions.find((x) => x.id === option.value)?.value ?? false
            }
            label=""
            onChange={(isChecked) => {
              const tempOptions = [...selectedOptions];
              const index = tempOptions.findIndex((x) => x.id === option.value);

              tempOptions[0].value = false;
              tempOptions[index].value = isChecked;

              let shouldSelectAllWards = true;

              tempOptions.forEach((option) => {
                if (option.value) {
                  shouldSelectAllWards = false;
                }
              });

              if (shouldSelectAllWards) {
                tempOptions[0].value = true;
              }

              setSelectedOptions(tempOptions);

              handleUserTracking();

              setUpdate(false);
              setTimeout(() => {
                setUpdate(true);
              }, 1);
            }}
          />
          <p>{option.label}</p>
        </WardOption>
      );
    });

    setOptions(optionsToSet);
  };

  useEffect(() => {
    if (selectedOptions.length === 0) {
      return;
    }

    const selectedIds: number[] = [];

    selectedOptions.forEach((option) => {
      if (option.id === "all" && option.value) {
        wardData.forEach((ward) => {
          selectedIds.push(parseInt(ward.value, 10));
        });
      }

      if (option.id !== "all" && option.value) {
        selectedIds.push(parseInt(option.id, 10));
      }
    });

    const orderIds = Array.from(new Set(selectedIds.sort((a, b) => a - b)));

    const portalPreferences = safeStorage.getItem(AUTH_PORTAL_PREFERENCES);
    let pendingPortalPreferences: IPortalPreferences | null = null;

    if (portalPreferences) {
      const parsedPortalPreferences = safeJsonParse<IPortalPreferences>(
        portalPreferences,
        {} as IPortalPreferences
      );

      if (parsedPortalPreferences.wards) {
        const preferencesWards = parsedPortalPreferences.wards;

        const orderPreferencesWards = Array.from(
          new Set(preferencesWards.sort((a, b) => a - b))
        );

        if (
          JSON.stringify(orderPreferencesWards) === JSON.stringify(orderIds) ||
          orderIds.length === 0 ||
          orderPreferencesWards.length === 0
        ) {
          return;
        }

        // Defer the localStorage + context update until the server confirms
        // and we trigger refetch — that way the column re-derivation (driven
        // by `applicableMeasurements`, which depends on `wards`) lands in
        // the same tick as the patient list entering its refetching state,
        // instead of running ahead of it.
        pendingPortalPreferences = {
          ...parsedPortalPreferences,
          wards: orderIds,
        };
      }
    }

    userService.updateWardsConfig(orderIds).then(() => {
      if (pendingPortalPreferences) {
        safeStorage.setItem(
          AUTH_PORTAL_PREFERENCES,
          JSON.stringify(pendingPortalPreferences)
        );
        changePortalPreferences(pendingPortalPreferences);
      }
      // Refetch every patient-list variant by prefix so the active page
      // (whichever variant it is) picks up the new ward filter. The hook's
      // own `refetch()` only targets the variant captured at render time
      // and was sometimes a no-op for the currently visible list.
      queryClient.refetchQueries({ queryKey: ["patient-list-active"] });
      queryClient.refetchQueries({ queryKey: ["patient-list-archived"] });
      queryClient.refetchQueries({ queryKey: ["patient-list-flagged"] });
      queryClient.refetchQueries({ queryKey: ["patient-list-alerts"] });
      queryClient.refetchQueries({ queryKey: ["patient-list-billing"] });
      queryClient.invalidateQueries({
        queryKey: PATIENT_SEARCH_LIST_QUERY_KEY,
      });

      // If the user is on a patient detail page (or any non-list page) the
      // changed ward filter no longer applies to what they're viewing, so
      // bounce them back to the patient list. Navigate straight to the
      // concrete list route in a single hop rather than to "/" — "/" is only
      // a `<Navigate to="/patients">` redirect, which is itself a
      // `<Navigate to="active">` redirect. When two ward-change effects race
      // (see the cascade in the `selectedOptions[0]?.value` effect below),
      // those effect-driven redirect elements can be left rendering `null`,
      // producing an intermittent blank screen (EN: fix-ward-blank-screen).
      // Going directly to `/patients/<variant>` is idempotent, stays inside
      // the already-mounted DashboardLayout, and has no null-rendering
      // intermediate route.
      // Decide from react-router's `location` (not `window.location` +
      // setTimeout) so the check reflects the current route deterministically.
      const segments = location.pathname.split("/");
      const firstSegment = segments[1];
      const secondSegment = segments[2];
      if (
        secondSegment !== "archived" &&
        secondSegment !== "active" &&
        secondSegment !== "flagged" &&
        secondSegment !== "alerts" &&
        secondSegment !== "billing" &&
        firstSegment !== "calendar"
      ) {
        const isMobileApp = safeStorage.getItem(IS_MOBILE_APP);
        if (!isMobileApp) {
          const variant = safeStorage.getItem(PATIENT_LIST_VARIANT) || "active";
          navigate(`/patients/${variant}`);
        }
      }
    });
  }, [selectedOptions]);

  useEffect(() => {
    wardsService.getAvailableWards().then((response) => {
      const { data } = response;

      data.forEach((ward) => {
        const doesHospitalIdExists = wardData.some(
          (item) => item.value === ward.wardId.toString()
        );

        if (!doesHospitalIdExists) {
          wardData.push({
            label: ward.wardName,
            value: ward.wardId.toString(),
            key: uuidv4(),
          });
        }
      });

      if (wardData.length > 1) {
        setShowMulitpleWards(true);
      } else {
        return;
      }

      const options: SelectedOption[] = [];

      const parsedPortalPreferences = safeJsonParse<IPortalPreferences>(
        safeStorage.getItem(AUTH_PORTAL_PREFERENCES),
        {} as IPortalPreferences
      );

      if (
        !parsedPortalPreferences.wards ||
        parsedPortalPreferences.wards.length === data.length ||
        parsedPortalPreferences.wards.length === 0
      ) {
        options.push({
          id: "all",
          value: true,
        });

        wardData.forEach((option) => {
          options.push({
            id: option.value,
            value: false,
          });
        });
      } else {
        options.push({
          id: "all",
          value: false,
        });

        wardData.forEach((option) => {
          options.push({
            id: option.value,
            value: parsedPortalPreferences.wards.includes(
              parseInt(option.value, 10)
            ),
          });
        });
      }

      setSelectedOptions(options);

      setUpdate(false);
      setTimeout(() => {
        setUpdate(true);
      }, 1);
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        showOptions &&
        optionRef.current &&
        !optionRef.current.contains(event.target)
      ) {
        setShowOptions(false);
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [showOptions]);

  useEffect(() => {
    if (!selectedOptions.length) {
      return;
    }

    const tempOptions = [...selectedOptions];

    if (selectedOptions[0].value === true) {
      selectedOptions
        .filter((v) => v.id !== "all")
        .forEach((option, index) => {
          tempOptions[index + 1].value = false;
        });
    }

    setSelectedOptions(tempOptions);

    setUpdate(false);
    setTimeout(() => {
      setUpdate(true);
    }, 1);
  }, [selectedOptions[0]?.value]);

  useEffect(() => {
    handleWardOptions();
  }, [update]);

  const getNameToDisplay = () => {
    const selectedLabels: string[] = [];
    const tempOptions = [...selectedOptions];

    tempOptions.forEach((option) => {
      if (option.value) {
        if (option.id !== "all") {
          const detailedOption = wardData.find((x) => x.value === option.id);

          if (detailedOption) {
            selectedLabels.push(detailedOption.label);
          }
        } else {
          selectedLabels.push(t("nav_allWards", { wards: wardLabel.plural }));
        }
      }
    });

    if (selectedLabels.length > 1) {
      selectedLabels.sort((a, b) => {
        return a.length - b.length;
      });
      return (
        <DisplayName>{`${selectedLabels[0]} (+${
          selectedLabels.length - 1
        })`}</DisplayName>
      );
    }

    return <DisplayName>{selectedLabels[0]}</DisplayName>;
  };

  const isMobileApp = safeStorage.getItem(IS_MOBILE_APP);
  if (isMobileApp) {
    return <Spin className={spinCss()} fullscreen size="large" />;
  }

  if (!leftPanelHidden) {
    return (
      <>
        <TopPanelMenuComponent onLogoClick={handlePatientListNavigation}>
          <NavContentContainer>
            <TopPanelItemsContainerDesktop>
              {renderTopBarMenuRoutes()}
            </TopPanelItemsContainerDesktop>
            <SearchBar />

            <SupportButton
              style={showMulitpleWards ? {} : { marginRight: "10px" }}
            />

            <ViewTypeSelector />

            {showMulitpleWards ? (
              <WardSelector
                style={{
                  borderColor: showOptions
                    ? "var(--colors-navbar-button-active-border)"
                    : "transparent",
                }}
                active={!selectedOptions[0]?.value}
                onClick={() => setShowOptions(true)}
              >
                {getNameToDisplay()} <ArrowDown />
                <OptionsContainer ref={optionRef} open={showOptions}>
                  {update ? options : null}
                </OptionsContainer>
              </WardSelector>
            ) : null}

            <TopPanelMenuMobileButtonComponent
              isMobileLeftPanelMenuOpen={isMobileLeftPanelMenuOpen}
              onClick={handleOnChangeMobileLeftPanelVisibility}
              smallScreenWidth={1100}
            />
          </NavContentContainer>

          <UserSettingsDesktop />
        </TopPanelMenuComponent>

        <LeftPanelMenu
          items={renderLeftPanelMenuByDevice()}
          onLeftPanelMenuItemClick={handleOnChangeMobileLeftPanelVisibility}
        />

        <LeftPanelUserSettings />

        <ContentContainer>
          {/* EN-2750: a page that throws mid-render used to take the whole
            tree down to a blank screen. Catching it here keeps the top panel,
            so the user can navigate away without reloading. */}
          <ErrorBoundary
            resetKey={location.pathname}
            fallback={<NotFoundContent reason="error" />}
          >
            {children ?? <Outlet />}
          </ErrorBoundary>
        </ContentContainer>
      </>
    );
  }

  return (
    <>
      <TopPanelMenuComponent onLogoClick={() => navigate("/")}>
        <NavContentContainer>
          <TopPanelItemsContainerDesktop>
            {renderTopBarMenuRoutes()}
          </TopPanelItemsContainerDesktop>

          <SearchBar />

          <SupportButton style={{ marginRight: "10px", marginLeft: "15px" }} />

          <TopPanelMenuMobileButtonComponent
            isMobileLeftPanelMenuOpen={isMobileLeftPanelMenuOpen}
            onClick={handleOnChangeMobileLeftPanelVisibility}
          />
        </NavContentContainer>

        <UserSettingsDesktop />
      </TopPanelMenuComponent>

      <LeftPanelUserSettings />

      <ContentContainer leftPanelHidden>
        {/* EN-2750: a page that throws mid-render used to take the whole tree
            down to a blank screen. Catching it here keeps the top panel, so
            the user can navigate away without reloading. */}
        <ErrorBoundary
          resetKey={location.pathname}
          fallback={<NotFoundContent reason="error" />}
        >
          {children ?? <Outlet />}
        </ErrorBoundary>
      </ContentContainer>
    </>
  );
}
